import { useCallback, useMemo, useRef, useState } from "react";
import { Sparkles, Upload } from "lucide-react";

import { NoxusMenu } from "@/components/UI/NoxusMenu";
import { SpotToast } from "@/components/SpotToast";
import type { SkillConfig } from "@/generated";
import { AddNewEntry } from "../AssistantDetailsContent";
import { EMPTY_SKILL } from "./skillMd";
import {
  buildSkillZip,
  extractSkillsFromZip,
  skillFolderName,
} from "./skillZip";
import { downloadBlob } from "@/utils/downloadBlob";
import { SkillCard } from "./SkillCard";
import { SkillEditorDialog } from "./SkillEditorDialog";

export const SkillsField = ({
  skills,
  onSkillsChange,
  isDisabled,
}: {
  skills: SkillConfig[] | undefined;
  onSkillsChange: (skills: SkillConfig[]) => void;
  isDisabled?: boolean;
}) => {
  const currentSkills = useMemo(() => skills ?? [], [skills]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // `index: null` is a skill being created — it only joins the list on save,
  // so cancelling out of a new skill leaves nothing behind.
  const [editing, setEditing] = useState<{
    index: number | null;
    skill: SkillConfig;
  } | null>(null);

  const update = useCallback(
    (next: SkillConfig[]) => {
      if (isDisabled) return;
      onSkillsChange(next);
    },
    [onSkillsChange, isDisabled]
  );

  const onAddSkill = useCallback(() => {
    if (isDisabled) return;
    setEditing({ index: null, skill: EMPTY_SKILL() });
  }, [isDisabled]);

  const onImportClick = useCallback(() => {
    if (isDisabled) return;
    fileInputRef.current?.click();
  }, [isDisabled]);

  const onZipUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset first so re-picking the same file still fires a change event.
      event.target.value = "";
      if (!file) return;

      try {
        const imported = await extractSkillsFromZip(file);
        update([...currentSkills, ...imported]);
        SpotToast.success(
          imported.length === 1
            ? `Imported "${imported[0].name}"`
            : `Imported ${imported.length} skills`
        );
      } catch (error) {
        SpotToast.error({
          title: "Could not import that ZIP",
          description:
            error instanceof Error
              ? error.message
              : "The file is not readable.",
        });
      }
    },
    [currentSkills, update]
  );

  const onExport = useCallback(async (skill: SkillConfig) => {
    try {
      downloadBlob(
        await buildSkillZip(skill),
        `${skillFolderName(skill.name)}.zip`
      );
    } catch {
      SpotToast.error("Could not export that skill");
    }
  }, []);

  return (
    <div className="flex flex-col gap-2">
      {currentSkills.map((skill, index) => (
        <SkillCard
          key={`${skill.name}-${index}`}
          skill={skill}
          isDisabled={isDisabled}
          onToggle={(enabled) =>
            update(
              currentSkills.map((s, i) => (i === index ? { ...s, enabled } : s))
            )
          }
          onEdit={() => setEditing({ index, skill })}
          onExport={() => onExport(skill)}
          onDelete={() => update(currentSkills.filter((_, i) => i !== index))}
        />
      ))}

      {/* AddNewEntry raises pointerdown on Enter/Space, which is what Radix
          opens on, so the menu needs no open state of its own. */}
      <NoxusMenu
        align="start"
        isDisabled={isDisabled}
        itemsArray={[
          { label: "New skill", icon: Sparkles, onClick: onAddSkill },
          { label: "Import from ZIP", icon: Upload, onClick: onImportClick },
        ]}
      >
        <AddNewEntry label="Add skill" isDisabled={isDisabled} />
      </NoxusMenu>

      <input
        ref={fileInputRef}
        type="file"
        accept=".zip"
        className="hidden"
        onChange={onZipUpload}
      />

      {editing && (
        <SkillEditorDialog
          // Remount per edit, so the dialog never carries a previous draft.
          key={editing.index ?? "new"}
          skill={editing.skill}
          originalName={editing.index === null ? undefined : editing.skill.name}
          otherNames={currentSkills
            .filter((_, i) => i !== editing.index)
            .map((s) => s.name)}
          isOpen
          isDisabled={isDisabled}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          onSave={(saved) =>
            update(
              editing.index === null
                ? [...currentSkills, saved]
                : currentSkills.map((s, i) => (i === editing.index ? saved : s))
            )
          }
        />
      )}
    </div>
  );
};
